import { useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { useLaborDevStatusPanelRegistration } from "@/lib/labor/laborDevStatusPanelContext";
import type { LaborStatusPanelProps } from "@/lib/labor/LaborStatusPanel";
import { laborRemotePersistenceDisabled } from "@/lib/labor/laborEnv";
import { isLaborOwnerDeparting, setLaborOwnerDeparting } from "@/lib/labor/laborOwnerDeparture";
import { playPairFailureSound } from "@/lib/floorStationPairAudio";
import { useLaborSession } from "@/lib/labor/useLaborSession";

import { inboundFloorLeavePlan, type InboundFloorLeaveReason } from "./inboundFloorLeave";

function leaveInboundFloor(
  navigate: ReturnType<typeof useNavigate>,
  reason: InboundFloorLeaveReason,
): void {
  const plan = inboundFloorLeavePlan({
    reason,
    ownerDeparting: isLaborOwnerDeparting(),
  });
  if (plan.playFailureSound) {
    playPairFailureSound();
  }
  if (plan.navigateHome) {
    navigate("/", { replace: true });
  }
}

/**
 * Clocks inbound floor working surfaces into hub `receiving` labor.
 * Holding / To stock pass `enabled` true (page load). Arrivals passes `enabled` only while
 * the log-arrival modal is open. Receive queue must not mount this tracker.
 */
export function InboundStationLaborTracker(props: {
  wirePage: string;
  workLabel: string;
  /** When false, do not auto-start and clock out if a session is open. Default true. */
  enabled?: boolean;
  /**
   * When false, intentional clock-out from `enabled` flipping off does not navigate to floor home.
   * Idle / remote end still leave the floor. Default true.
   */
  leaveFloorOnStop?: boolean;
}): JSX.Element | null {
  const navigate = useNavigate();
  const laborEnabled = props.enabled !== false && props.wirePage !== "";
  const leaveFloorOnStop = props.leaveFloorOnStop !== false;
  const laborEnabledRef = useRef(laborEnabled);
  const suppressLeaveOnStopRef = useRef(false);

  const labor = useLaborSession({
    page: props.wirePage,
    domain: "shipping",
    moduleGate: "shipping",
    devPersistenceDisabled: () => laborRemotePersistenceDisabled(),
    workLabel: props.workLabel,
    autoStart: laborEnabled,
    requirePersistedClockIn: true,
    onIdleTimeout: () => leaveInboundFloor(navigate, "idle"),
    onPersistedSessionLost: () => {
      if (isLaborOwnerDeparting() || suppressLeaveOnStopRef.current) return;
      leaveInboundFloor(navigate, "session_lost");
    },
    onSessionEndedRemotely: () => {
      if (isLaborOwnerDeparting() || suppressLeaveOnStopRef.current) return;
      leaveInboundFloor(navigate, "ended_remotely");
    },
    onStopped: () => {
      if (isLaborOwnerDeparting() || suppressLeaveOnStopRef.current || !leaveFloorOnStop) return;
      leaveInboundFloor(navigate, "stopped");
    },
    localOnlyMessage: (reason) =>
      reason === "read-only"
        ? "Started working. Your role is read-only for Shipping, so this is a local timer only."
        : "Started working. Labor writes are blocked, so this is a local timer only.",
  });

  useEffect(() => {
    if (laborEnabledRef.current && !laborEnabled && labor.isWorking) {
      if (!isLaborOwnerDeparting()) {
        const intentionalStopWithoutLeave = !leaveFloorOnStop;
        if (intentionalStopWithoutLeave) {
          suppressLeaveOnStopRef.current = true;
          setLaborOwnerDeparting(true);
        }
        void labor.stopWorking().finally(() => {
          if (intentionalStopWithoutLeave) {
            suppressLeaveOnStopRef.current = false;
            setLaborOwnerDeparting(false);
          }
        });
      }
    }
    laborEnabledRef.current = laborEnabled;
  }, [laborEnabled, labor.isWorking, labor.stopWorking, leaveFloorOnStop]);

  const snapshot = useMemo((): LaborStatusPanelProps => {
    return {
      isWorking: labor.isWorking,
      elapsedSeconds: labor.elapsedSeconds,
      workLabel: props.workLabel,
      banner: labor.banner,
      lastSummary: labor.lastSummary,
      heartbeatIntervalMs: labor.heartbeatIntervalMs,
      idleTimeoutMs: labor.idleTimeoutMs,
    };
  }, [
    labor.isWorking,
    labor.elapsedSeconds,
    labor.banner,
    labor.lastSummary,
    labor.heartbeatIntervalMs,
    labor.idleTimeoutMs,
    props.workLabel,
  ]);

  useLaborDevStatusPanelRegistration(import.meta.env.DEV, snapshot);
  return null;
}
